/**
 * Lottieアニメーションの遅延読み込み・再生制御
 *
 * @description [data-lottie] にJSONのURLを持つ要素を対象に、
 *              画面に近づいたタイミングで初めてプレイヤー本体（動的import）と
 *              JSONを取得し、画面外に出たら一時停止して負荷を抑える。
 *              プレイヤーを動的importにすることで初期バンドルから切り離している。
 *
 *              [data-lottie-once] が付いた要素はループせず、再生タイミングも
 *              ここでは決めない（読み込みだけ面倒を見る）。「いつ流すか」は
 *              ページ側のモジュールが playLottieOnce() を呼んで指定する。
 */

/** ループせず、再生タイミングをページ側が持つ要素の目印 */
const ONCE_HOOK = "data-lottie-once"

/** 読み込み済みのアニメーションを要素ごとに保持する */
const players = new WeakMap<HTMLElement, any>()

/** 動きを減らす設定のときは1フレーム目だけ表示して再生しない */
const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches

/** lottie本体の読み込みPromise（一度だけ読む） */
let lottieLib: Promise<any> | null = null

/**
 * lottieプレイヤー本体を読み込む（初回のみ）
 * @returns {Promise<any>} lottieのモジュール
 */
const loadLib = (): Promise<any> => {
  if (!lottieLib) {
    lottieLib = import(/* webpackChunkName: "lottie" */ "lottie-web/build/player/lottie_light").then(
      (m: any) => m.default ?? m
    )
  }
  return lottieLib
}

/**
 * 対象要素にLottieを読み込む（初回のみ）
 * @param {HTMLElement} el - [data-lottie] を持つ要素
 * @returns {Promise<any>} lottieのアニメーションインスタンス
 */
const load = async (el: HTMLElement): Promise<any> => {
  const existing = players.get(el)
  if (existing) return existing

  const path = el.dataset.lottie
  if (!path) return null

  const lottie = await loadLib()

  // 並行呼び出しで二重生成しないよう再確認する
  const created = players.get(el)
  if (created) return created

  const anim = lottie.loadAnimation({
    container: el,
    renderer: "svg",
    loop: !el.hasAttribute(ONCE_HOOK),
    autoplay: false,
    path,
    rendererSettings: {
      progressiveLoad: true,
      preserveAspectRatio: "xMidYMid meet",
    },
  })

  players.set(el, anim)
  return anim
}

/**
 * 対象のLottieを頭出しして1回だけ流す
 * @description [data-lottie-once] を付けた要素の再生タイミングをページ側から指定するための入り口。
 *              まだ読み込めていなければ読み込みを待ってから再生する。
 * @param {HTMLElement} el - [data-lottie] を持つ要素
 * @returns {Promise<void>}
 * @example
 * playLottieOnce(card.querySelector("[data-lottie]"))
 */
export const playLottieOnce = async (el: HTMLElement): Promise<void> => {
  if (reduceMotion) return

  const anim = await load(el)
  if (!anim) return

  anim.goToAndPlay(0, true)
}

/**
 * Lottie要素を初期化する
 * @returns {void}
 */
const initLottie = (): void => {
  const targets = document.querySelectorAll<HTMLElement>("[data-lottie]")
  if (!targets.length) return

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach(async (entry) => {
        const el = entry.target as HTMLElement
        const anim = await load(el)
        if (!anim) return

        // 1回再生のものは読み込みだけ担当し、再生はページ側の playLottieOnce() に任せる
        if (el.hasAttribute(ONCE_HOOK)) return

        // 読み込み待ちの間に画面外へ出ている場合があるので、都度現在の状態で判定する
        if (entry.isIntersecting) {
          if (!reduceMotion) anim.play()
        } else {
          anim.pause()
        }
      })
    },
    { rootMargin: "20% 0px" }
  )

  targets.forEach((el) => observer.observe(el))
}

initLottie()
